import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { useApplyTenantBranding } from "@/contexts/branding";
import { API, formatApiError } from "@/lib/api";
import { fullUrl } from "@/components/FileUploader";

export default function PublicNPS() {
  const { slug, token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/nps/${slug}/${token}`);
        setInfo(data);
        if (data.already_responded) setDone({ bucket: data.score >= 9 ? "promoter" : data.score >= 7 ? "passive" : "detractor" });
      } catch (e) {
        setErr("Pesquisa inválida ou expirada.");
      } finally { setLoading(false); }
    })();
  }, [slug, token]);

  useApplyTenantBranding(info?.tenant?.branding);

  const submit = async (e) => {
    e.preventDefault();
    if (score === null) return;
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/public/nps/${slug}/${token}`, { score, comment });
      setDone(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  if (err && !info) return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="p-8 rounded-2xl max-w-md text-center">
        <div className="text-destructive font-display text-xl mb-2">Ops…</div>
        <div className="text-sm text-muted-foreground">{err}</div>
        <Link to="/" className="inline-flex items-center gap-1.5 mt-6 text-sm underline"><ArrowLeft className="w-3 h-3" /> Página inicial</Link>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden text-tenant-foreground"
        style={{ background: `hsl(var(--tenant-primary))` }}>
        <div className="absolute inset-0 swiss-grid opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          {info?.tenant?.branding?.logo_url ? (
            <div className="w-12 h-12 rounded-lg bg-white/15 grid place-items-center overflow-hidden shrink-0" data-testid="nps-tenant-logo">
              <img
                src={fullUrl(info.tenant.branding.logo_url)}
                alt={info.tenant.branding?.display_name || info.tenant.legal_name || "logo"}
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                  // Falha ao carregar → substitui pelo fallback da letra inicial.
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.textContent = (info?.tenant?.branding?.display_name || info?.tenant?.legal_name || "·").charAt(0).toUpperCase();
                    parent.classList.add("font-display", "font-bold", "text-xl");
                  }
                }}
              />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-lg bg-white/15 grid place-items-center font-display font-bold text-xl">
              {(info?.tenant?.branding?.display_name || info?.tenant?.legal_name || "·").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="font-display font-bold text-xl">{info?.tenant?.branding?.display_name || info?.tenant?.legal_name}</div>
        </div>
        <div className="relative max-w-md space-y-4">
          <div className="text-xs uppercase tracking-[0.25em] opacity-70 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Pesquisa de Satisfação
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight">Sua opinião faz a diferença, {info?.client_name?.split(" ")[0] || "cliente"}.</h1>
          <p className="text-base opacity-90 leading-relaxed">
            Em uma escala de 0 a 10, quanto você recomendaria nossos serviços para um amigo ou familiar?
          </p>
        </div>
        <div className="relative text-xs opacity-60">Leva menos de 30 segundos.</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        {done ? (
          <Card className="w-full max-w-md p-8 rounded-tenant border-border text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">Obrigado pela resposta!</h2>
            <p className="text-sm text-muted-foreground">Seu feedback foi registrado e nos ajuda a melhorar continuamente.</p>
            {(() => {
              const googleUrl = info?.tenant?.branding?.google_review_url;
              if (!googleUrl) return null;
              // Promoter (9-10) → CTA forte. Passivo (7-8) → CTA gentil.
              // Detrator (<7) → não mostra Google (evita review pública ruim).
              const isPromoter = done.bucket === "promoter";
              const isPassive = done.bucket === "passive";
              if (!isPromoter && !isPassive) return null;
              return (
                <div className="mt-6 pt-6 border-t border-border" data-testid="nps-google-cta">
                  <p className="text-sm text-muted-foreground mb-3">
                    {isPromoter
                      ? "Que tal deixar essa avaliação também no Google? Faz uma diferença enorme pra gente 💛"
                      : "Se quiser, deixe também uma avaliação no Google — ajuda muito nosso trabalho."}
                  </p>
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="nps-google-link"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md font-medium text-sm transition-transform hover:scale-[1.02]"
                    style={{
                      background: `hsl(var(--tenant-primary))`,
                      color: `hsl(var(--tenant-primary-foreground))`,
                    }}
                  >
                    <Star className="w-4 h-4 fill-current" />
                    Avaliar no Google
                  </a>
                </div>
              );
            })()}
          </Card>
        ) : (
          <Card className="w-full max-w-md p-8 rounded-tenant border-border">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Sua nota</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight mb-6">De 0 a 10, qual sua nota?</h2>
            <form onSubmit={submit} className="space-y-6">
              <div className="grid grid-cols-11 gap-1.5">
                {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                  <button key={n} type="button" onClick={() => setScore(n)}
                    data-testid={`nps-score-${n}`}
                    className={`aspect-square rounded-md border-2 font-display font-bold text-base transition-all
                      ${score === n
                        ? "scale-110 shadow-lg"
                        : "border-border bg-card hover:border-tenant"}`}
                    style={score === n ? {
                      background: `hsl(var(--tenant-primary))`,
                      borderColor: `hsl(var(--tenant-primary))`,
                      color: `hsl(var(--tenant-primary-foreground))`,
                    } : {}}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground -mt-3">
                <span>Improvável</span><span>Muito provável</span>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Conta pra gente o motivo? (opcional)</label>
                <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="O que mais você gostou? O que podemos melhorar?" data-testid="nps-comment" />
              </div>
              {err && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">{err}</div>}
              <Button type="submit" disabled={busy || score === null} className="w-full h-11 gap-2"
                data-testid="nps-submit"
                style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                Enviar avaliação
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
}
